import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import CardViewer from 'components/field/CardViewer';
import Form from 'components/form/Form';
import Alert from 'components/global/Alert';
import Radio from 'components/global/Radio';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import fetchUserDetails from 'services/common/fetchUserDetails';
import { PAYMENT } from 'services/consts';

import { getClientCards } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

import { setManageSubscriptionReload } from '../../SubscriptionAccount/SubscriptionAccountSlice';
import * as S from './ChangePaymentMethodModal.styled';
import DeletePaymentMethodModal from './DeletePaymentMethodModal';

export default function SelectPaymentMethodModal({
	nextStep,
	subscriptionCardID,
	setCardLastFour,
	setChangeCardStatus,
	cancelDowngradeRequest
}) {
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const modal = useModal();
	const { showError, showInfo } = useAlert();

	const { clientCards, planName: currentPlanName } = useSelector((state) => state.user);

	const [isLoading, setIsLoading] = useState(false);
	const [cardLoading, setCardLoading] = useState(false);
	const [selectedCard, setSelectedCard] = useState(subscriptionCardID);

	const getClientCardsOfCompany = useCallback(async (forceSync = false) => {
		setCardLoading(true);
		await dispatch(
			getClientCards({
				isCvvRequire: false,
				forceSync
			})
		)
			.unwrap()
			.finally(() => {
				setCardLoading(false);
			});
	}, []);

	useEffect(() => {
		getClientCardsOfCompany();
	}, []);

	const cardRadioChange = ({ currentTarget: { id } }) => {
		setSelectedCard(Number(id));
	};

	const changePaymentMethod = async () => {
		setIsLoading(true);
		const URL = cancelDowngradeRequest ? PAYMENT.CANCEL_PACKAGE_DOWNGRADE : PAYMENT.CHANGE_SUBSCRIPTION_CARD;

		if (selectedCard !== undefined && selectedCard !== null) {
			try {
				const response = await axios.post(URL, {
					clientCardID: Number(selectedCard)
				});

				if (response.status === 200) {
					dispatch(setManageSubscriptionReload(true));
					setChangeCardStatus(response.data.status);
					setCardLastFour(clientCards?.find((card) => card.ID === selectedCard).cardLastFour);
					fetchUserDetails();
					nextStep();
				} else {
					AppLogger.error(response);
				}
			} catch (error) {
				if (error.response?.data.code === 'error.P003') {
					showError({
						message: 'error.tapPaymentErrorPleaseContactOurTeam'
					});
				} else if (error.response?.data.code === 'error.P008') {
					showError({
						message: 'error.databaseInvalidErrorPleaseContactOurTeam'
					});
				} else if (error.response?.data.code === 'error.S005') {
					showError({
						message: 'error.subscriptionCannotBeCreatedPleaseContactOurTeam'
					});
				} else if (error.response?.data.code === 'error.S006') {
					showError({
						message: 'error.currentSubscriptionOfTheClientCouldNotBeFoundPleaseContactOurTeam'
					});
				} else if (error.response?.data.code === 'error.S007') {
					showError({
						message: 'error.clientCardNotSelected'
					});
				} else {
					showError();
					AppLogger.error(error);
				}
			} finally {
				setIsLoading(false);
			}
		} else {
			setIsLoading(false);
			showInfo({
				header: 'pleaseSelectCardHeader',
				message: 'pleaseSelectCardMessage'
			});
		}
	};

	return (
		<>
			<ModalContent>
				<Form borderless className="mt-5 mb-5" style={{ justifyContent: 'center', gap: '1.5rem' }}>
					{cancelDowngradeRequest && (
						<Alert
							content="cancelDowngradeRequestInfoText"
							className="mb-3"
							style={{ width: '60rem' }}
							showIcon
						/>
					)}

					{!cardLoading &&
						clientCards?.map((card, index) => (
							<S.ItemBox key={index}>
								<S.ItemContainer id={card.ID} onClick={cardRadioChange}>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										{currentPlanName !== 'FreePackage' && (
											<Radio className="mx-2" checked={selectedCard === card.ID} readOnly />
										)}

										<CardViewer lastFour={card.cardLastFour} cardBrand={card.cardBrand} />

										{card.ID === subscriptionCardID && (
											<S.Badge>
												<Typography variant="caption" fontWeight="500" color="var(--blue)">
													primary
												</Typography>
											</S.Badge>
										)}
									</div>
									{!cancelDowngradeRequest && (
										<Button
											iconButton
											icon={faTrash}
											variant="transparent"
											iconSize="small"
											color="var(--black)"
											disabled={card.ID === subscriptionCardID}
											disabledTooltip="youCantDeletePrimaryCard"
											onClick={() => {
												{
													modal.remove();
													NiceModal.show(DeletePaymentMethodModal, {
														removeCard: clientCards[index],
														onSuccess: () => getClientCardsOfCompany(true)
													});
												}
											}}
										/>
									)}
								</S.ItemContainer>
							</S.ItemBox>
						))}
					{cardLoading &&
						Array(2)
							.fill(0)
							.map((_, index) => (
								<S.ItemBox key={index}>
									<S.ItemContainer>
										<Skeleton width="60rem" height="5rem" />
									</S.ItemContainer>
								</S.ItemBox>
							))}
				</Form>
			</ModalContent>

			<ModalActions>
				<Button variant="outline" label="cancel" onClick={() => modal.remove()} />

				{currentPlanName !== 'FreePackage' && (
					<Button
						variant="blue"
						label="confirm"
						icon={faCheck}
						loading={isLoading}
						disabled={selectedCard === subscriptionCardID && !cancelDowngradeRequest}
						onClick={changePaymentMethod}
					/>
				)}
			</ModalActions>
		</>
	);
}
